import { gql } from '@apollo/client';
import { pathOr } from 'ramda';

import { dateFormatShort, universalDateFormatter } from '@utils/DateAndTime';
import { useIntl } from '@utils/intl';
import { useMyBuildings } from '@providers/Buildings';
import { useCurrentDate } from '@views/Calendar/hooks';
import { useErrorQuery } from '@providers/Errors';
import { getDeskSlots } from '@views/Book/Bookings/Desks/hooks';

const getTotalDesks = gql`
  query getCurrentBuildingInfo(
    $id: ID!
    $date: Date!
    $canBookMeetingRoom: Boolean!
    $canBookPhoneBox: Boolean!
    $canBookParkingSpot: Boolean!
    $canBookDesk: Boolean!
  ) {
    building: buildingInfo(buildingId: $id, date: $date) {
      numberOfMeetingRooms @include(if: $canBookMeetingRoom)
      numberOfFreeSpacesRooms @include(if: $canBookParkingSpot)
      numberOfTotalParkings @include(if: $canBookParkingSpot)
      numberOfAvailableDesksHourlyByAreaType @include(if: $canBookDesk) {
        areaType
        desksAvailable
        freeSlots
        totalSlots
      }
      numberOfTelefonBoxes @include(if: $canBookPhoneBox)
    }
  }
`;

interface DesksHourlyByType {
  areaType: string;
  desksAvailable: number;
  freeSlots: number;
  totalSlots: number;
}

interface Data {
  building: {
    id: string;
    numberOfAvailableDesksHourlyByAreaType: DesksHourlyByType[];
    numberOfMeetingRooms: number;
    numberOfFreeSpacesRooms: number;
    numberOfTotalParkings: number;
    numberOfTelefonBoxes: number;
  };
}

/*{
  canBookMeetingRoom,
      canBookPhoneBox,
      canBookParkingSpot,
      canBookDesk: canBookDesk || canBookFreeSpace,
}*/
export function useBookings(permissions) {
  const { t } = useIntl();
  const date = useCurrentDate();
  const { selectedBuildingId: buildingId } = useMyBuildings();
  const { canBookDesk, canBookFreeSpace } = permissions;
  const { data, loading, error } = useErrorQuery<Data>(getTotalDesks, {
    variables: {
      id: buildingId,
      date: universalDateFormatter({ date, format: dateFormatShort }),
      ...permissions,
      canBookDesk: canBookDesk || canBookFreeSpace,
    },
    skip: !buildingId,
    finderError: {
      type: 'fatal',
      message: t('views.Home.Bookings.Desks.hooks.desksDataFail'),
    },
  });

  const numberOfDesks = getDeskSlots(data?.building, 'desksAvailable', 0);
  const numberOfFreeSpacesRooms = pathOr(
    0,
    ['building', 'numberOfFreeSpacesRooms'],
    data,
  );
  const numberOfMeetingRooms = pathOr(
    0,
    ['building', 'numberOfMeetingRooms'],
    data,
  );
  const numberOfTotalParking = pathOr(
    0,
    ['building', 'numberOfTotalParkings'],
    data,
  );
  const numberOfTelefonBoxes = pathOr(
    0,
    ['building', 'numberOfTelefonBoxes'],
    data,
  );

  return {
    numberOfDesks,
    numberOfMeetingRooms,
    numberOfFreeSpacesRooms,
    numberOfTotalParking,
    numberOfTelefonBoxes,
    loading: !buildingId || loading,
    error,
  };
}
